const RATE_LIMIT_WINDOW_MS = 10 * 60 * 1000; // 10 minutes
const MAX_ATTEMPTS = 3;

interface RateLimitEntry {
  attempts: number;
  firstAttempt: number;
  blockedUntil: number | null;
}

function getKey(formId: string): string {
  return `rl_${formId}`;
}

function getEntry(formId: string): RateLimitEntry {
  try {
    const raw = sessionStorage.getItem(getKey(formId));
    if (raw) return JSON.parse(raw);
  } catch {}
  return { attempts: 0, firstAttempt: 0, blockedUntil: null };
}

function setEntry(formId: string, entry: RateLimitEntry) {
  sessionStorage.setItem(getKey(formId), JSON.stringify(entry));
}

export function checkRateLimit(formId: string): { allowed: boolean; message?: string } {
  const now = Date.now();
  const entry = getEntry(formId);

  // If blocked, check if block has expired
  if (entry.blockedUntil && now < entry.blockedUntil) {
    return { allowed: false, message: 'Too many attempts. Please try again in a few minutes.' };
  }

  // Reset if window expired
  if (entry.blockedUntil && now >= entry.blockedUntil) {
    setEntry(formId, { attempts: 0, firstAttempt: 0, blockedUntil: null });
    return { allowed: true };
  }

  // Reset if outside window
  if (entry.firstAttempt && now - entry.firstAttempt > RATE_LIMIT_WINDOW_MS) {
    setEntry(formId, { attempts: 0, firstAttempt: 0, blockedUntil: null });
    return { allowed: true };
  }

  return { allowed: true };
}

export function recordAttempt(formId: string): { blocked: boolean; message?: string } {
  const now = Date.now();
  const entry = getEntry(formId);

  // Reset if outside window
  if (entry.firstAttempt && now - entry.firstAttempt > RATE_LIMIT_WINDOW_MS) {
    setEntry(formId, { attempts: 1, firstAttempt: now, blockedUntil: null });
    return { blocked: false };
  }

  const newAttempts = entry.attempts + 1;
  const firstAttempt = entry.firstAttempt || now;

  if (newAttempts >= MAX_ATTEMPTS) {
    setEntry(formId, { attempts: newAttempts, firstAttempt, blockedUntil: now + RATE_LIMIT_WINDOW_MS });
    return { blocked: true, message: 'Too many attempts. Please try again in a few minutes.' };
  }

  setEntry(formId, { attempts: newAttempts, firstAttempt, blockedUntil: null });
  return { blocked: false };
}

/** Returns true if the honeypot field has a value (i.e., a bot filled it in) */
export function isHoneypotTriggered(value: string | undefined | null): boolean {
  return !!value && value.trim().length > 0;
}
